import { type ChangeEvent, type RefObject, useCallback, useMemo, useRef, useState } from "react";

import { myLensesActionType } from "../../analytics/event-creators";
import { useBlizzardEvents } from "../../analytics/hooks";
import { useSelectedFeature } from "../../shared/hooks";
import { debounce } from "../../shared/utils/event";
import { TagsValidation } from "../models";
import { validateTagDupesAndLimit, validateTagInputValue } from "../validation";

import { useGetTrendingTags } from "./useGetTrendingTags";

const makeTrendingTagLabels = (tag: string): { set: string; remove: string } => ({
    set: `trending_tag_set_${tag.toLowerCase()}`,
    remove: `trending_tag_remove_${tag.toLowerCase()}`,
});

export const useTagWithValidation = (
    tags: readonly string[],
    addTag: (name: string) => void,
    removeTag: (name: string) => void,
): {
    inputRef: RefObject<HTMLInputElement>;
    tagsValidation: TagsValidation;
    onInputTagChange: (event: ChangeEvent<HTMLInputElement>) => void;
    addTagFromInputField: (tagName: string) => void;
    addTagPreValidated: (tagName: string) => void; // used for trending tags table, where tags are pre-validated
    removeTagWithValidation: (tagName: string) => void; // removes validation error, if removing the tag fixes it
} => {
    const { data: trendingTags = [] } = useGetTrendingTags();
    const { sendBlizzardEvent } = useBlizzardEvents();
    const { selectedFeature } = useSelectedFeature();

    const trendingTagsSet = useMemo(() => new Set(trendingTags.map((tag) => tag.tag)), [trendingTags]);

    const inputRef = useRef<HTMLInputElement>(null);
    const [tagsValidation, setTagsValidation] = useState(TagsValidation.Valid);

    const onInputTagChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
        const inputElement = event.target;
        const validationResult = validateTagInputValue(inputElement, inputElement.value, tags);

        setTagsValidation(validationResult);
    }, 200);

    const addTagFromInputField = useCallback(
        (tagName: string) => {
            if (!inputRef.current) {
                return;
            }
            const inputElement = inputRef.current;
            const validationResult = validateTagInputValue(inputElement, tagName, tags);

            if (validationResult) {
                setTagsValidation(validationResult);
            } else {
                addTag(tagName);
                inputElement.value = "";
                inputElement.focus();
            }
        },
        [tags, inputRef, addTag],
    );

    const addTagPreValidated = useCallback(
        (tagName: string) => {
            const validationResult = validateTagDupesAndLimit(tagName, tags)(TagsValidation.Valid);

            if (validationResult) {
                setTagsValidation(validationResult);
            } else {
                if (trendingTagsSet.has(tagName)) {
                    const { set } = makeTrendingTagLabels(tagName);
                    sendBlizzardEvent({
                        event: "MYLENSES_ACTION",
                        actionType: myLensesActionType.click,
                        label: set,
                        namespace: selectedFeature ?? "",
                    });
                }
                addTag(tagName);
            }
        },
        [tags, addTag, setTagsValidation, trendingTagsSet, sendBlizzardEvent, selectedFeature],
    );

    const removeTagWithValidation = useCallback(
        (tagName: string) => {
            const nextTags = tags.filter((tag) => tag !== tagName);
            const nextValidationResult = validateTagDupesAndLimit(tagName, nextTags)(TagsValidation.Valid);

            // removing tag can fix:
            // tag limit exceeded
            // tag already exists
            if (
                (tagsValidation === TagsValidation.LimitReached || tagsValidation === TagsValidation.Exist) &&
                nextValidationResult === TagsValidation.Valid
            ) {
                setTagsValidation(TagsValidation.Valid);
            }

            if (trendingTagsSet.has(tagName)) {
                const { remove } = makeTrendingTagLabels(tagName);
                sendBlizzardEvent({
                    event: "MYLENSES_ACTION",
                    actionType: myLensesActionType.click,
                    label: remove,
                    namespace: selectedFeature ?? "",
                });
            }

            removeTag(tagName);
        },
        [tagsValidation, removeTag, tags, setTagsValidation, trendingTagsSet, sendBlizzardEvent, selectedFeature],
    );

    return {
        addTagFromInputField,
        addTagPreValidated,
        removeTagWithValidation,
        tagsValidation,
        onInputTagChange,
        inputRef,
    };
};
